import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckSquare, Lock, Shield } from "lucide-react";

const Login = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const redirectUrl = `${window.location.origin}/dashboard`;
  const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLogin = () => {
    window.location.href = authUrl;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950" data-testid="login-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="bg-gray-900 rounded-2xl shadow-2xl p-8 border border-gray-800">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-emerald-500/10 rounded-2xl p-4">
              <CheckSquare className="w-12 h-12 text-emerald-400" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-center text-white mb-2" data-testid="login-title">
            TaskFlow
          </h1>
          <p className="text-center text-gray-400 mb-8" data-testid="login-subtitle">
            Notion-style task management made simple
          </p>

          {/* Login Button */}
          <Button
            onClick={handleLogin}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-base py-6 mb-6"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>

          {/* Domain Restriction Notice */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6" data-testid="domain-restriction-notice">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-400 mb-1">
                  Restricted Access
                </p>
                <p className="text-sm text-yellow-500/80">
                  Only <span className="font-semibold">@emergent.sh</span> email addresses are allowed to access this application.
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <div className="bg-emerald-500/10 rounded-full p-1.5">
                <CheckSquare className="w-4 h-4 text-emerald-400" />
              </div>
              <span>Organize tasks with Kanban boards</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <div className="bg-emerald-500/10 rounded-full p-1.5">
                <CheckSquare className="w-4 h-4 text-emerald-400" />
              </div>
              <span>Track priorities and due dates</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <div className="bg-emerald-500/10 rounded-full p-1.5">
                <Lock className="w-4 h-4 text-emerald-400" />
              </div>
              <span>Secure authentication with Google</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Built with Emergent Auth
        </p>
      </div>
    </div>
  );
};

export default Login;
